import type { ReactNode } from "react";
import { Button } from "./Button";

export function LoadingState({ label = "Loading…" }: { label?: string }) {
  return (
    <div className="ods-state" role="status" aria-live="polite">
      <div className="ods-skeleton" style={{ width: 220, height: 14 }} />
      <div className="ods-skeleton" style={{ width: 160, height: 14 }} />
      <span className="ods-visually-hidden">{label}</span>
    </div>
  );
}

export function SkeletonRows({ rows = 5 }: { rows?: number }) {
  return (
    <div className="ods-stack" aria-hidden="true" style={{ padding: "var(--ods-space-4)" }}>
      {Array.from({ length: rows }).map((_, i) => (
        <div key={i} className="ods-skeleton" style={{ height: 18 }} />
      ))}
    </div>
  );
}

export function EmptyState({
  icon = "🗂️",
  title,
  description,
  action,
}: {
  icon?: string;
  title: string;
  description?: string;
  action?: ReactNode;
}) {
  return (
    <div className="ods-state">
      <div className="ods-state__icon" aria-hidden="true">
        {icon}
      </div>
      <h3 className="ods-state__title">{title}</h3>
      {description ? <p style={{ maxWidth: 420, margin: 0 }}>{description}</p> : null}
      {action}
    </div>
  );
}

export function ErrorState({ title = "Something went wrong", description, onRetry }: { title?: string; description?: string; onRetry?: () => void }) {
  return (
    <div className="ods-state" role="alert">
      <div className="ods-state__icon" aria-hidden="true">
        ⚠️
      </div>
      <h3 className="ods-state__title">{title}</h3>
      {description ? <p style={{ maxWidth: 420, margin: 0 }}>{description}</p> : null}
      {onRetry ? (
        <Button variant="secondary" size="sm" onClick={onRetry}>
          Try again
        </Button>
      ) : null}
    </div>
  );
}

export function AccessDeniedState({ reason }: { reason?: string }) {
  return (
    <EmptyState
      icon="🔒"
      title="Access denied"
      description={reason ?? "You don't have permission to view this. Ask an organization admin for access."}
    />
  );
}

export function EntitlementDeniedState({ product = "This application", onUpgrade }: { product?: string; onUpgrade?: () => void }) {
  return (
    <div className="ods-state">
      <div className="ods-state__icon" aria-hidden="true">
        ⭐
      </div>
      <h3 className="ods-state__title">{product} isn&apos;t enabled</h3>
      <p style={{ maxWidth: 440, margin: 0 }}>
        Your organization&apos;s current plan doesn&apos;t include this. Upgrade to unlock it.
      </p>
      {onUpgrade ? <Button onClick={onUpgrade}>View plans</Button> : null}
    </div>
  );
}

export function OfflineBanner() {
  return (
    <div className="ods-failclosed" role="status" style={{ borderStyle: "solid" }}>
      You appear to be offline. Some data may be stale until the connection returns.
    </div>
  );
}
